/**
 * MyReportsPage — Full-page resident portal for viewing own submitted reports.
 *
 * Route: /my-reports
 *
 * Steps:
 *   phone  → enter phone number, request SMS OTP
 *   otp    → enter 6-digit code, get resident JWT
 *   list   → browse own submitted reports
 *   detail → view full report + timeline + staff comments
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ArrowLeft, Phone, KeyRound, ChevronRight, RefreshCw, Clock,
    CheckCircle2, Circle, AlertCircle, MessageSquare, FileText,
    LogOut, MapPin, CalendarDays, Hash,
} from 'lucide-react';
import RequestDetailMap from '../components/RequestDetailMap';

// ── Types ──────────────────────────────────────────────────────────────────

interface MyRequest {
    service_request_id: string;
    service_name: string;
    description: string | null;
    status: 'open' | 'in_progress' | 'closed';
    address: string | null;
    requested_datetime: string | null;
    updated_datetime: string | null;
    completion_message: string | null;
}

interface MyRequestDetail extends MyRequest {
    lat: number | null;
    long: number | null;
    comments: { id: number; content: string; username: string; created_at: string }[];
}

// ── Constants ──────────────────────────────────────────────────────────────

const TOKEN_KEY = 'resident_portal_token';
const PHONE_KEY = 'resident_portal_phone';

const STATUS_LABEL: Record<string, string> = {
    open: 'Open',
    in_progress: 'In Progress',
    closed: 'Resolved',
};

const STATUS_STYLE: Record<string, string> = {
    open: 'bg-amber-100 text-amber-800 border border-amber-200',
    in_progress: 'bg-blue-100 text-blue-800 border border-blue-200',
    closed: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
};

// ── Helpers ────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLE[status] ?? 'bg-gray-100 text-gray-600'}`}>
            {STATUS_LABEL[status] ?? status}
        </span>
    );
}

function formatDate(iso: string | null) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric', month: 'long', day: 'numeric',
    });
}

async function apiPost(path: string, body: unknown) {
    const res = await fetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || 'Request failed');
    return data;
}

async function apiGet(path: string, token: string) {
    const res = await fetch(path, {
        headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(`${res.status}: ${data.detail || 'Request failed'}`);
    return data;
}

// ── Step views ─────────────────────────────────────────────────────────────

type Step = 'phone' | 'otp' | 'list' | 'detail';

export default function MyReportsPage() {
    const navigate = useNavigate();

    const [step, setStep] = useState<Step>('phone');
    const [phone, setPhone] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [token, setToken] = useState<string | null>(null);
    const [requests, setRequests] = useState<MyRequest[]>([]);
    const [detail, setDetail] = useState<MyRequestDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [resendCooldown, setResendCooldown] = useState(0);

    // Restore session
    useEffect(() => {
        const savedToken = localStorage.getItem(TOKEN_KEY);
        const savedPhone = localStorage.getItem(PHONE_KEY);
        if (savedToken && savedPhone) {
            setToken(savedToken);
            setPhone(savedPhone);
            setStep('list');
            loadRequests(savedToken);
        }
    }, []);

    // Cooldown timer
    useEffect(() => {
        if (resendCooldown <= 0) return;
        const t = setInterval(() => setResendCooldown(c => c - 1), 1000);
        return () => clearInterval(t);
    }, [resendCooldown]);

    async function loadRequests(tok: string) {
        setLoading(true);
        setError(null);
        try {
            const data = await apiGet('/api/portal/my-requests', tok);
            setRequests(data);
        } catch (e: unknown) {
            if (e instanceof Error && e.message.includes('401')) {
                logout();
            } else {
                setError(e instanceof Error ? e.message : 'Failed to load reports');
            }
        } finally {
            setLoading(false);
        }
    }

    async function loadDetail(id: string, tok: string) {
        setLoading(true);
        setError(null);
        try {
            const data = await apiGet(`/api/portal/my-requests/${id}`, tok);
            setDetail(data);
            setStep('detail');
        } catch (e: unknown) {
            if (e instanceof Error && e.message.includes('401')) {
                logout();
            } else {
                setError(e instanceof Error ? e.message : 'Failed to load report');
            }
        } finally {
            setLoading(false);
        }
    }

    async function handleRequestOTP() {
        if (!phone.trim()) return;
        setLoading(true);
        setError(null);
        try {
            await apiPost('/api/portal/auth/request-otp', { phone: phone.trim() });
            setStep('otp');
            setResendCooldown(60);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to send SMS');
        } finally {
            setLoading(false);
        }
    }

    async function handleVerifyOTP() {
        if (otpCode.length !== 6) return;
        setLoading(true);
        setError(null);
        try {
            const data = await apiPost('/api/portal/auth/verify-otp', {
                phone: phone.trim(),
                code: otpCode,
            });
            const tok = data.access_token;
            setToken(tok);
            localStorage.setItem(TOKEN_KEY, tok);
            localStorage.setItem(PHONE_KEY, phone.trim());
            setStep('list');
            await loadRequests(tok);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Invalid or expired code');
        } finally {
            setLoading(false);
        }
    }

    function logout() {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(PHONE_KEY);
        setToken(null);
        setPhone('');
        setOtpCode('');
        setRequests([]);
        setDetail(null);
        setStep('phone');
    }

    function handleBack() {
        if (step === 'detail') { setStep('list'); setDetail(null); }
        else if (step === 'otp') { setStep('phone'); }
        else { navigate('/'); }
    }

    // ── Layout ────────────────────────────────────────────────────────────

    return (
        <div className="min-h-screen flex flex-col" style={{ background: '#f7f9fc' }}>

            {/* ── Top bar ── */}
            <header style={{ background: 'linear-gradient(135deg, #0f1a47 0%, #1a3060 50%, #0f1a47 100%)' }}>
                <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleBack}
                            className="p-2 rounded-xl text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                            aria-label="Back"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-primary-400" />
                            <span className="text-white font-semibold text-base">My Reports</span>
                        </div>
                    </div>

                    {token && (
                        <button
                            onClick={logout}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-white/60 hover:text-white hover:bg-white/10 text-sm transition-colors"
                        >
                            <LogOut className="w-4 h-4" />
                            <span className="hidden sm:inline">Sign out</span>
                        </button>
                    )}
                </div>
            </header>

            {/* ── Content ── */}
            <main className="flex-1 flex flex-col items-center px-4 py-8 md:py-12">
                <div className="w-full max-w-2xl">
                    <AnimatePresence mode="wait">

                        {/* ── Phone step ── */}
                        {step === 'phone' && (
                            <motion.div key="phone"
                                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}
                            >
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                    <div className="px-8 pt-10 pb-8 text-center space-y-3">
                                        <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center"
                                            style={{ background: 'linear-gradient(135deg, #ecfdf6, #d1fae5)' }}>
                                            <Phone className="w-8 h-8 text-emerald-600" />
                                        </div>
                                        <h1 className="text-xl font-bold text-gray-900">View Your Submitted Reports</h1>
                                        <p className="text-gray-500 text-sm max-w-sm mx-auto">
                                            Enter the phone number you used when submitting your report.
                                            We'll send you a one-time SMS code to log in.
                                        </p>
                                    </div>

                                    <div className="px-8 pb-8 space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                                Phone number
                                            </label>
                                            <input
                                                type="tel"
                                                value={phone}
                                                onChange={e => { setPhone(e.target.value); setError(null); }}
                                                onKeyDown={e => e.key === 'Enter' && handleRequestOTP()}
                                                placeholder="+960 XXXXXXX"
                                                autoFocus
                                                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-gray-900 text-base focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition"
                                            />
                                        </div>

                                        {error && (
                                            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 rounded-xl px-4 py-3 border border-red-100">
                                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                                {error}
                                            </div>
                                        )}

                                        <button
                                            onClick={handleRequestOTP}
                                            disabled={loading || !phone.trim()}
                                            className="w-full py-3.5 rounded-xl font-semibold text-white text-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                            style={{ background: 'linear-gradient(135deg, #0fb37a, #0d9e6d)' }}
                                        >
                                            {loading
                                                ? <><RefreshCw className="w-4 h-4 animate-spin" /> Sending SMS…</>
                                                : <><ChevronRight className="w-4 h-4" /> Send SMS Code</>
                                            }
                                        </button>

                                        <p className="text-center text-xs text-gray-400">
                                            Your data is private.{' '}
                                            <Link to="/privacy" className="underline hover:text-gray-600">Privacy policy</Link>
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* ── OTP step ── */}
                        {step === 'otp' && (
                            <motion.div key="otp"
                                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}
                            >
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                    <div className="px-8 pt-10 pb-8 text-center space-y-3">
                                        <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center"
                                            style={{ background: 'linear-gradient(135deg, #eff6ff, #dbeafe)' }}>
                                            <KeyRound className="w-8 h-8 text-blue-600" />
                                        </div>
                                        <h1 className="text-xl font-bold text-gray-900">Enter Your Code</h1>
                                        <div>
                                            <p className="text-gray-500 text-sm">We sent a 6-digit code via SMS to</p>
                                            <p className="text-gray-800 font-semibold mt-0.5">{phone}</p>
                                        </div>
                                    </div>

                                    <div className="px-8 pb-8 space-y-4">
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={6}
                                            value={otpCode}
                                            onChange={e => { setOtpCode(e.target.value.replace(/\D/g, '')); setError(null); }}
                                            onKeyDown={e => e.key === 'Enter' && handleVerifyOTP()}
                                            placeholder="000000"
                                            autoFocus
                                            className="w-full px-4 py-4 rounded-xl border border-gray-200 text-gray-900 text-3xl text-center font-mono tracking-[0.6em] focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition"
                                        />

                                        {error && (
                                            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 rounded-xl px-4 py-3 border border-red-100">
                                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                                {error}
                                            </div>
                                        )}

                                        <button
                                            onClick={handleVerifyOTP}
                                            disabled={loading || otpCode.length !== 6}
                                            className="w-full py-3.5 rounded-xl font-semibold text-white text-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                            style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)' }}
                                        >
                                            {loading
                                                ? <><RefreshCw className="w-4 h-4 animate-spin" /> Verifying…</>
                                                : <><CheckCircle2 className="w-4 h-4" /> Verify &amp; Continue</>
                                            }
                                        </button>

                                        <button
                                            onClick={() => { if (resendCooldown === 0) handleRequestOTP(); }}
                                            disabled={resendCooldown > 0 || loading}
                                            className="w-full py-2 text-sm text-gray-400 hover:text-gray-600 disabled:text-gray-300 flex items-center justify-center gap-1.5 transition-colors"
                                        >
                                            <Clock className="w-3.5 h-3.5" />
                                            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {/* ── List step ── */}
                        {step === 'list' && (
                            <motion.div key="list"
                                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}
                                className="space-y-4"
                            >
                                {/* Summary bar */}
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900">Your Reports</h2>
                                        <p className="text-xs text-gray-400 mt-0.5">
                                            Showing reports linked to <span className="font-medium text-gray-600">{phone}</span>
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => token && loadRequests(token)}
                                        disabled={loading}
                                        className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-white border border-gray-200 transition"
                                        title="Refresh"
                                    >
                                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                                    </button>
                                </div>

                                {/* Loading */}
                                {loading && (
                                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex items-center justify-center py-20">
                                        <RefreshCw className="w-7 h-7 animate-spin text-emerald-400" />
                                    </div>
                                )}

                                {/* Error */}
                                {!loading && error && (
                                    <div className="bg-white rounded-2xl border border-red-100 shadow-sm px-6 py-8 text-center">
                                        <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                                        <p className="text-red-600 text-sm">{error}</p>
                                    </div>
                                )}

                                {/* Empty state */}
                                {!loading && !error && requests.length === 0 && (
                                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-6 py-16 text-center space-y-3">
                                        <Circle className="w-12 h-12 text-gray-200 mx-auto" />
                                        <p className="text-gray-700 font-medium">No reports found</p>
                                        <p className="text-gray-400 text-sm">
                                            No reports submitted with this phone number in the last 12 months.
                                        </p>
                                        <Link to="/"
                                            className="inline-flex items-center gap-1.5 mt-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium">
                                            <ChevronRight className="w-4 h-4" />
                                            Submit a new report
                                        </Link>
                                    </div>
                                )}

                                {/* Report cards */}
                                {!loading && requests.length > 0 && (
                                    <div className="space-y-3">
                                        {requests.map(req => (
                                            <button
                                                key={req.service_request_id}
                                                onClick={() => token && loadDetail(req.service_request_id, token)}
                                                className="w-full text-left bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all p-5 group"
                                            >
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="flex-1 min-w-0 space-y-2">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <StatusBadge status={req.status} />
                                                            <span className="text-xs text-gray-400 font-mono">
                                                                #{req.service_request_id.slice(-8).toUpperCase()}
                                                            </span>
                                                        </div>
                                                        <p className="font-semibold text-gray-900 text-sm">{req.service_name}</p>
                                                        {req.description && (
                                                            <p className="text-gray-500 text-sm line-clamp-2">{req.description}</p>
                                                        )}
                                                        {req.address && (
                                                            <p className="text-gray-400 text-xs flex items-center gap-1">
                                                                <MapPin className="w-3 h-3 flex-shrink-0" />
                                                                {req.address}
                                                            </p>
                                                        )}
                                                        <p className="text-gray-400 text-xs flex items-center gap-1">
                                                            <CalendarDays className="w-3 h-3 flex-shrink-0" />
                                                            {formatDate(req.requested_datetime)}
                                                        </p>
                                                    </div>
                                                    <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-emerald-500 flex-shrink-0 mt-1 transition-colors" />
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </motion.div>
                        )}

                        {/* ── Detail step ── */}
                        {step === 'detail' && detail && (
                            <motion.div key="detail"
                                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}
                                className="space-y-4"
                            >
                                {/* Header card */}
                                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <StatusBadge status={detail.status} />
                                            <span className="text-xs font-mono text-gray-400 flex items-center gap-1">
                                                <Hash className="w-3 h-3" />
                                                {detail.service_request_id.slice(-12).toUpperCase()}
                                            </span>
                                        </div>
                                        <h2 className="text-lg font-bold text-gray-900">{detail.service_name}</h2>
                                        {detail.address && (
                                            <p className="text-sm text-gray-500 flex items-center gap-1.5">
                                                <MapPin className="w-4 h-4 flex-shrink-0 text-gray-400" />
                                                {detail.address}
                                            </p>
                                        )}
                                        <p className="text-xs text-gray-400 flex items-center gap-1.5">
                                            <CalendarDays className="w-3.5 h-3.5 flex-shrink-0" />
                                            Submitted {formatDate(detail.requested_datetime)}
                                        </p>
                                    </div>

                                    {detail.description && (
                                        <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
                                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Description</p>
                                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{detail.description}</p>
                                        </div>
                                    )}

                                    {detail.status === 'closed' && detail.completion_message && (
                                        <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4">
                                            <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-1.5">Resolution</p>
                                            <p className="text-sm text-emerald-800">{detail.completion_message}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Map */}
                                {detail.lat != null && detail.long != null && (
                                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                        <div className="px-6 py-3 border-b border-gray-50">
                                            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Location</h3>
                                        </div>
                                        <div className="h-56 w-full">
                                            <RequestDetailMap
                                                lat={detail.lat}
                                                lng={detail.long}
                                                matchedAsset={null}
                                                mapLayers={[]}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Timeline */}
                                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-5">Timeline</h3>
                                    <div className="relative space-y-5">
                                        {/* vertical line */}
                                        <div className="absolute left-[14px] top-6 bottom-0 w-px bg-gray-100" />

                                        {/* Submitted */}
                                        <TimelineRow
                                            icon={<CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                                            iconBg="bg-emerald-100"
                                            label="Report submitted"
                                            date={detail.requested_datetime}
                                        />

                                        {/* Status update */}
                                        {detail.updated_datetime && detail.updated_datetime !== detail.requested_datetime && (
                                            <TimelineRow
                                                icon={<RefreshCw className="w-4 h-4 text-blue-600" />}
                                                iconBg="bg-blue-100"
                                                label={`Status updated to ${STATUS_LABEL[detail.status] ?? detail.status}`}
                                                date={detail.updated_datetime}
                                            />
                                        )}

                                        {/* Comments */}
                                        {detail.comments.map(c => (
                                            <TimelineRow
                                                key={c.id}
                                                icon={<MessageSquare className="w-4 h-4 text-amber-600" />}
                                                iconBg="bg-amber-100"
                                                label={c.content}
                                                sublabel={c.username}
                                                date={c.created_at}
                                            />
                                        ))}

                                        {detail.comments.length === 0 && detail.updated_datetime === detail.requested_datetime && (
                                            <p className="text-xs text-gray-400 pl-9">No updates yet. We'll process your report soon.</p>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        )}

                    </AnimatePresence>
                </div>
            </main>

            {/* ── Footer ── */}
            <footer className="py-6 text-center text-xs text-gray-400 border-t border-gray-100">
                <Link to="/" className="hover:text-gray-600 transition-colors">← Back to portal</Link>
                <span className="mx-3 text-gray-200">|</span>
                <Link to="/privacy" className="hover:text-gray-600 transition-colors">Privacy policy</Link>
            </footer>
        </div>
    );
}

// ── Timeline row sub-component ─────────────────────────────────────────────

function TimelineRow({
    icon,
    iconBg,
    label,
    sublabel,
    date,
}: {
    icon: React.ReactNode;
    iconBg: string;
    label: string;
    sublabel?: string;
    date: string | null;
}) {
    return (
        <div className="relative flex gap-4">
            <div className={`relative z-10 flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${iconBg}`}>
                {icon}
            </div>
            <div className="flex-1 min-w-0 pt-0.5">
                {sublabel && (
                    <p className="text-xs font-semibold text-gray-500 mb-0.5">{sublabel}</p>
                )}
                <p className="text-sm text-gray-700">{label}</p>
                <p className="text-xs text-gray-400 mt-1">{formatDate(date)}</p>
            </div>
        </div>
    );
}
